import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin } from "lucide-react";

const INTERNAL_LEGAL_PATHS = {
  "https://portal.pttnz.net/privacy": "/privacy",
  "https://portal.pttnz.net/security": "/security",
  "https://portal.pttnz.net/terms": "/terms",
};

// Lightweight inline markdown: **bold** and [label](url). Internal legal-page
// links render as client-side <Link>, everything else as a new-tab <a>.
const renderInline = (text) => {
  const regex = /\*\*(.+?)\*\*|\[([^\]]+)\]\(([^)]+)\)/g;
  const parts = [];
  let last = 0;
  let match;
  let key = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    if (match[1] !== undefined) {
      parts.push(<strong key={key++} className="font-semibold text-[var(--ptt-ink)]">{match[1]}</strong>);
    } else {
      const label = match[2];
      const url = match[3];
      const internal = INTERNAL_LEGAL_PATHS[url];
      parts.push(
        internal ? (
          <Link key={key++} to={internal} className="text-[var(--ptt-teal-deep)] font-semibold signal-underline">{label}</Link>
        ) : (
          <a key={key++} href={url} target="_blank" rel="noopener noreferrer" className="text-[var(--ptt-teal-deep)] font-semibold signal-underline">{label}</a>
        )
      );
    }
    last = regex.lastIndex;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
};

const Block = ({ block, testId }) => {
  if (block.type === "p") {
    return <p className="text-[var(--ptt-ink-soft)] leading-relaxed" data-testid={testId}>{renderInline(block.text)}</p>;
  }
  if (block.type === "ul") {
    return (
      <ul className="space-y-2.5 pl-1" data-testid={testId}>
        {block.items.map((item, i) => (
          <li key={i} className="flex items-start gap-2.5 text-[var(--ptt-ink-soft)] leading-relaxed">
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--ptt-teal)] mt-2 shrink-0" />
            <span>{renderInline(item)}</span>
          </li>
        ))}
      </ul>
    );
  }
  if (block.type === "contact") {
    return (
      <div className="border border-[var(--ptt-line)] bg-white p-6 space-y-3" data-testid={testId}>
        <div className="font-display font-bold">{block.name}</div>
        <div className="flex items-start gap-2.5 text-sm text-[var(--ptt-ink-soft)]"><MapPin size={15} className="mt-0.5 text-[var(--ptt-teal-deep)] shrink-0" />{block.address}</div>
        <div className="flex items-start gap-2.5 text-sm text-[var(--ptt-ink-soft)]"><Mail size={15} className="mt-0.5 text-[var(--ptt-teal-deep)] shrink-0" /><a href={`mailto:${block.email}`} className="hover:text-[var(--ptt-teal-deep)]">{block.email}</a></div>
        <div className="flex items-start gap-2.5 text-sm text-[var(--ptt-ink-soft)]"><Phone size={15} className="mt-0.5 text-[var(--ptt-teal-deep)] shrink-0" /><a href={`tel:${block.phoneHref}`} className="hover:text-[var(--ptt-teal-deep)]">{block.phone}</a></div>
      </div>
    );
  }
  return null;
};

const LegalPage = ({ eyebrow, title, lastUpdated, intro, sections, relatedLinks }) => (
  <div data-testid="legal-page">
    <section className="bg-[var(--ptt-paper)] border-b border-[var(--ptt-line)]">
      <div className="max-w-4xl mx-auto px-5 lg:px-10 pt-16 pb-10">
        <div className="eyebrow">{eyebrow}</div>
        <h1 className="font-display text-4xl lg:text-5xl font-bold mt-3 leading-[1.05] tracking-tight" data-testid="legal-page-title">{title}</h1>
        <div className="font-mono-tech text-xs uppercase tracking-widest text-[var(--ptt-muted)] mt-4" data-testid="legal-page-last-updated">Last updated: {lastUpdated}</div>
        {intro && <p className="text-[var(--ptt-ink-soft)] leading-relaxed mt-6 max-w-2xl" data-testid="legal-page-intro">{renderInline(intro)}</p>}
        {relatedLinks && relatedLinks.length > 0 && (
          <div className="mt-6 flex flex-wrap gap-3" data-testid="legal-page-related-links">
            {relatedLinks.map((l) => (
              <Link key={l.to} to={l.to} className="text-sm font-semibold text-[var(--ptt-teal-deep)] signal-underline" data-testid={`legal-related-link-${l.to.replace(/\//g, "")}`}>{l.label}</Link>
            ))}
          </div>
        )}
      </div>
    </section>

    <section className="max-w-4xl mx-auto px-5 lg:px-10 py-14 lg:py-16 space-y-12">
      {sections.map((s) => (
        <div key={s.num} id={`section-${s.num}`} data-testid={`legal-section-${s.num}`}>
          <h2 className="font-display text-xl lg:text-2xl font-bold leading-snug">
            <span className="text-[var(--ptt-teal-deep)]">{s.num}.</span> {s.title}
          </h2>
          <div className="mt-4 space-y-4">
            {s.blocks.map((b, i) => (
              <Block key={i} block={b} testId={`legal-section-${s.num}-block-${i}`} />
            ))}
          </div>
        </div>
      ))}
    </section>

    <section className="max-w-4xl mx-auto px-5 lg:px-10 pb-16 lg:pb-20">
      <div className="border-t border-[var(--ptt-line)] pt-8 font-mono-tech text-xs uppercase tracking-widest text-[var(--ptt-muted)]" data-testid="legal-page-copyright">
        © {new Date().getFullYear()} PTT NZ Limited. All rights reserved.
      </div>
    </section>
  </div>
);

export default LegalPage;
